import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Package, DollarSign, Calendar, Truck, Search, Eye, ChevronLeft, ChevronRight, Filter, X, ChevronDown } from 'lucide-react';
import EntryFormMulti from '../components/EntryFormMulti';
import { entriesService } from '../services/entriesService';

const EntriesPage = ({ products, suppliers }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntries, setFilteredEntries] = useState([]);
  // La página se deriva de los searchParams: la URL es la única fuente de verdad
  const page = Number(searchParams.get('page') ?? 1);
  const [itemsPerPage] = useState(30);
  const [filters, setFilters] = useState({
    product: '',
    supplier: '',
    dateRange: '',
    costRange: '',
    startDate: '',
    endDate: ''
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'entry_date',
    direction: 'desc'
  });
  const dropdownRefs = useRef({});

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Cargar entradas al montar el componente
  useEffect(() => {
    console.log('🚀 EntriesPage useEffect ejecutándose...');
    
    const loadEntries = async () => {
      try {
        setLoading(true);
        console.log('🔄 Iniciando carga de entradas...');
        
        // Usar los parámetros por defecto del servicio
        const response = await entriesService.getAll(1, 100000);
        console.log('📊 Respuesta entradas:', response);
  
        // Extraer los datos según el formato de respuesta
        let data = [];
        if (response && response.success && response.data) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (response && Array.isArray(response)) {
          data = response;
        }
        
        console.log('✅ Entradas procesadas:', data.length, data);
        setEntries(data);
      } catch (error) {
        console.error('❌ Error cargando entradas:', error);
        console.error('❌ Stack trace:', error.stack);
        console.error('❌ Error response:', error.response);
        
        // Intentar cargar entradas vacías para no romper la UI
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    loadEntries();
  }, []);

  // Filtrar entradas basado en el término de búsqueda y filtros
  // prevFiltersRef detecta cambios reales de filtros/orden/búsqueda
  const prevFiltersRef = useRef({ searchTerm, filters, sortConfig });

  useEffect(() => {
    let filtered = entries;

    // Filtro de búsqueda general
    if (searchTerm.trim()) {
      filtered = filtered.filter(entry => {
        // En el modo multi-producto, buscamos en el supplier_name y otros campos generales
        const supplierName = entry.supplier_name || getSupplierName(entry.supplier_id);
        const searchLower = searchTerm.toLowerCase();
        
        // Buscar en campos de la entrada
        const matchesEntry = supplierName.toLowerCase().includes(searchLower) ||
               entry.invoice_number?.toLowerCase().includes(searchLower) ||
               entry.notes?.toLowerCase().includes(searchLower);
        
        // Buscar en los nombres de productos
        const matchesProducts = entry.products && entry.products.some(product => 
          product.product_name?.toLowerCase().includes(searchLower)
        );
        
        return matchesEntry || matchesProducts;
      });
    }

    // Filtro por proveedor
    if (filters.supplier) {
      filtered = filtered.filter(entry => {
        const supplierName = entry.supplier_name || getSupplierName(entry.supplier_id);
        return supplierName.toLowerCase().includes(filters.supplier.toLowerCase());
      });
    }

    // Filtro por rango de fecha
    if (filters.dateRange) {
      const today = new Date();
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.entry_date);
        switch (filters.dateRange) {
          case 'today':
            return entryDate.toDateString() === today.toDateString();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            return entryDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(today.getMonth() - 1);
            return entryDate >= monthAgo;
          case 'year':
            const yearAgo = new Date(today);
            yearAgo.setFullYear(today.getFullYear() - 1);
            return entryDate >= yearAgo;
          default:
            return true;
        }
      });
    }

    // Filtro por rango de costo
    if (filters.costRange) {
      filtered = filtered.filter(entry => {
        // En el modo multi-producto, usamos total_cost de la entrada
        const cost = parseFloat(entry.total_cost) || 0;
        switch (filters.costRange) {
          case 'bajo':
            return cost < 1000;
          case 'medio':
            return cost >= 1000 && cost < 5000;
          case 'alto':
            return cost >= 5000;
          default:
            return true;
        }
      });
    }

    // Filtro por fechas específicas
    if (filters.startDate) {
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.entry_date);
        const startDate = new Date(filters.startDate);
        return entryDate >= startDate;
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.entry_date);
        const endDate = new Date(filters.endDate);
        return entryDate <= endDate;
      });
    }

    // Aplicar ordenamiento
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Manejar valores nulos/undefined
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Para fechas
        if (sortConfig.key === 'entry_date') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        // Para números
        else if (sortConfig.key === 'total_cost' || sortConfig.key === 'product_count') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        // Para strings
        else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredEntries(filtered);

    // Detectar si los filtros/búsqueda/orden cambiaron realmente
    const filtersChanged =
      prevFiltersRef.current.searchTerm !== searchTerm ||
      prevFiltersRef.current.filters !== filters ||
      prevFiltersRef.current.sortConfig !== sortConfig;

    // Solo resetear page si el usuario cambió los filtros (no por remounts o cambios de datos)
    if (filtersChanged) {
      try {
        if (searchParams.get('page') !== '1') {
          setSearchParams({ page: '1' }, { replace: true });
        }
      } catch (e) {
        console.warn('No se pudo actualizar page en la URL al aplicar filtros', e);
      }
    }

    // Actualizar prevFiltersRef al final
    prevFiltersRef.current = { searchTerm, filters, sortConfig };
  }, [entries, searchTerm, filters, products, suppliers, sortConfig]);

  const handleViewEntry = (entry) => {
    navigate(`/entries/entry/${entry.id}?page=${page}`);
  };

  const handleAddEntry = () => {
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDeleteEntry = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta entrada?')) {
      try {
        setLoading(true);
        await entriesService.delete(id);
        
        // Actualizar la lista de entradas
        const updatedEntries = await entriesService.getAll(1, 100000);
        setEntries(updatedEntries.data || []);
      } catch (error) {
        console.error('Error eliminando entrada:', error);
        alert('Error al eliminar la entrada: ' + (error.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveEntry = async (entryData) => {
    try {
      setLoading(true);
      console.log('Guardando entrada con datos:', entryData);
      
      if (!entryData.products || !Array.isArray(entryData.products) || entryData.products.length === 0) {
        throw new Error('La entrada debe tener al menos un producto');
      }
      
      // Validar datos mínimos requeridos
      if (!entryData.entry_date) {
        throw new Error('La fecha de entrada es obligatoria');
      }
      
      // Usar el servicio para crear una nueva entrada
      const response = await entriesService.create(entryData);
      console.log('Respuesta al crear entrada:', response);
      
      // Recargar las entradas para mostrar la nueva entrada
      const updatedEntries = await entriesService.getAll(1, 100);
      setEntries(updatedEntries.data || []);
      
      setShowForm(false);
      setEditingEntry(null);
    } catch (error) {
      console.error('Error al guardar la entrada:', error);
      let errorMessage = 'Error desconocido al guardar la entrada';
      
      if (error.response) {
        // El servidor respondió con un código de error
        console.error('Respuesta de error del servidor:', error.response);
        errorMessage = `Error del servidor (${error.response.status}): ${error.response.data?.error || error.message}`;
      } else if (error.request) {
        // La solicitud fue hecha pero no se recibió respuesta
        console.error('No se recibió respuesta del servidor:', error.request);
        errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
      } else {
        // Algo ocurrió en la configuración de la solicitud que causó un error
        errorMessage = error.message || 'Error al preparar la solicitud';
      }
      
      alert('Error al guardar la entrada: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      product: '',
      supplier: '',
      dateRange: '',
      costRange: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const toggleDropdown = (columnName) => {
    setActiveDropdown(activeDropdown === columnName ? null : columnName);
  };

  const getUniqueSuppliers = () => {
    const supplierNames = [...new Set(filteredEntries.map(entry => 
      entry.supplier_name || getSupplierName(entry.supplier_id)
    ))];
    return supplierNames.filter(name => name && name !== 'Desconocido');
  };

  // Función genérica para obtener valores únicos de cualquier campo
  const getUniqueValues = (fieldName) => {
    const values = [...new Set(entries.map(entry => entry[fieldName]).filter(Boolean))];
    return values.sort();
  };

  // Cálculos de paginación (la página se deriva de la URL)
  const totalEntries = filteredEntries.length;
  const totalPages = Math.ceil(totalEntries / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEntries = filteredEntries.slice(startIndex, endIndex);

  // Función para cambiar de página y actualizar URL
  const handlePageChange = (newPage) => {
    setSearchParams({ page: String(newPage) });
  };

  // Si la URL no tiene page al montar, restaurarla desde sessionStorage (solo una vez)
  useEffect(() => {
    try {
      // usar has() para evitar falsos negativos cuando searchParams existe pero aún no contiene 'page'
      if (!searchParams.has('page')) {
        const saved = sessionStorage.getItem('entries_page');
        if (saved) {
          // usar replace para no añadir historial intermedio
          setSearchParams({ page: saved }, { replace: true });
        }
      }
    } catch (e) {
      // En caso de error (p.ej. entorno sin sessionStorage), no romper la UI
      console.warn('Error restaurando page desde sessionStorage', e);
    }
  }, []);

  // Guardar la página actual en sessionStorage cuando cambie (page es la fuente de verdad)
  useEffect(() => {
    try {
      sessionStorage.setItem('entries_page', String(page));
    } catch (e) {
      console.warn('No se pudo guardar entries_page en sessionStorage', e);
    }
  }, [page]);

  // Mostrar loading si no hay productos o proveedores cargados
  if (!products || !Array.isArray(products) || !suppliers || !Array.isArray(suppliers)) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Cargando datos del sistema...</p>
        </div>
      </motion.div>
    );
  }

  const getSupplierName = (supplierId) => {
    if (!suppliers || !Array.isArray(suppliers)) return 'Cargando...';
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : 'Desconocido';
  };

  // Componente ColumnFilter
  const ColumnFilter = ({ columnName, title, filterKey, options, currentValue, sortKey, isDate = false }) => {
    return (
      <div className="relative" ref={el => dropdownRefs.current[columnName] = el}>
        <button
          onClick={() => toggleDropdown(columnName)}
          className="flex items-center justify-between w-full text-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
        >
          <span className="flex items-center gap-1">
            {title}
            {sortKey && (
              <span className="text-blue-600 font-bold">
                {getSortIcon(sortKey)}
              </span>
            )}
          </span>
          <ChevronDown 
            className={`w-3 h-3 ml-1 transition-transform ${
              activeDropdown === columnName ? 'rotate-180' : ''
            } ${currentValue ? 'text-blue-600' : ''}`}
          />
        </button>
        
        <AnimatePresence>
          {activeDropdown === columnName && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[250px]"
            >
              <div className="p-2 max-h-80 overflow-y-auto">
                {/* Opciones de ordenamiento */}
                {sortKey && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-100">
                      📊 Ordenar
                    </div>
                    <button
                      onClick={() => {
                        handleSort(sortKey);
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <span className="text-blue-600">↑</span>
                      A → Z (Ascendente)
                    </button>
                    <button
                      onClick={() => {
                        setSortConfig({ key: sortKey, direction: 'desc' });
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <span className="text-blue-600">↓</span>
                      Z → A (Descendente)
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                  </>
                )}

                {/* Filtros de fecha personalizados */}
                {isDate && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      📅 Filtrar por Fechas
                    </div>
                    <div className="px-3 py-2">
                      <label className="block text-xs text-gray-600 mb-1">Fecha Desde:</label>
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="px-3 py-2">
                      <label className="block text-xs text-gray-600 mb-1">Fecha Hasta:</label>
                      <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    {(filters.startDate || filters.endDate) && (
                      <button
                        onClick={() => {
                          handleFilterChange('startDate', '');
                          handleFilterChange('endDate', '');
                          setActiveDropdown(null);
                        }}
                        className="w-full mx-3 my-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Limpiar fechas
                      </button>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                  </>
                )}

                {/* Opciones de filtrado predefinidas */}
                <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  🔍 Filtros Rápidos
                </div>
                <button
                  onClick={() => {
                    handleFilterChange(filterKey, '');
                    setActiveDropdown(null);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                    !currentValue ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {filterKey === 'product' ? '📦 Todos los productos' : 
                   filterKey === 'supplier' ? '🏪 Todos los proveedores' :
                   filterKey === 'dateRange' ? '📅 Todas las fechas' :
                   '💰 Todos los costos'}
                </button>
                
                <div className="border-t border-gray-100 my-1"></div>
                
                {options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handleFilterChange(filterKey, option.value);
                      setActiveDropdown(null);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                      currentValue === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="text-4xl font-extrabold text-gray-800 mb-8 text-center bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        Registro de Entradas
      </h2>

      {/* Barra de búsqueda */}
      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por producto, proveedor, factura..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 text-gray-700 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Contador de filtros activos */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex justify-center mb-4">
          <motion.button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Filter className="w-4 h-4" />
            {getActiveFiltersCount()} filtro{getActiveFiltersCount() > 1 ? 's' : ''} activo{getActiveFiltersCount() > 1 ? 's' : ''}
            <X className="w-4 h-4 ml-1" />
            Limpiar
          </motion.button>
        </div>
      )}

      <div className="flex justify-center gap-4 mb-8">
        <motion.button
          onClick={handleAddEntry}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <PlusCircle className="w-5 h-5" />
          Nueva Entrada
        </motion.button>
      </div>

      <AnimatePresence mode="wait">
        {showForm && (
          <EntryFormMulti
            currentEntry={editingEntry}
            onSave={handleSaveEntry}
            onCancel={handleCancelForm}
            products={products}
            suppliers={suppliers}
          />
        )}
      </AnimatePresence>

      {!showForm && (
        <motion.div
          className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-full mx-auto border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {currentEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium">
                {searchTerm || getActiveFiltersCount() > 0 ? 'No se encontraron entradas que coincidan con los filtros.' : 'No hay entradas registradas. ¡Es hora de recibir material!'}
              </p>
            </div>
          ) : (
            <div className="w-full">
              <div className="mb-4 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  📥 Entradas Registradas ({totalEntries})
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Página {page} de {totalPages} • Mostrando {startIndex + 1}-{Math.min(endIndex, totalEntries)} de {totalEntries} entradas
                </p>
              </div>
              <div className="overflow-x-auto shadow-lg rounded-lg">
                <table className="w-full table-auto divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="id"
                          title="ID"
                          filterKey="id"
                          currentValue={filters.id}
                          options={[]}
                          sortKey="id"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="products"
                          title="Productos"
                          filterKey="product_count"
                          currentValue={filters.product_count}
                          options={[]}
                          sortKey="product_count"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="cost"
                          title="Valor Total"
                          filterKey="costRange"
                          currentValue={filters.costRange}
                          options={[
                            { value: 'bajo', label: '💰 Bajo (< $1,000)' },
                            { value: 'medio', label: '💰 Medio ($1,000-$5,000)' },
                            { value: 'alto', label: '💰 Alto (> $5,000)' }
                          ]}
                          sortKey="total_cost"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="date"
                          title="Fecha"
                          filterKey="dateRange"
                          currentValue={filters.dateRange}
                          options={[
                            { value: 'today', label: '📅 Hoy' },
                            { value: 'week', label: '📅 Esta semana' },
                            { value: 'month', label: '📅 Este mes' },
                            { value: 'year', label: '📅 Este año' }
                          ]}
                          sortKey="entry_date"
                          isDate={true}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="supplier"
                          title="Proveedor"
                          filterKey="supplier"
                          currentValue={filters.supplier}
                          options={getUniqueSuppliers().map(supplier => ({ value: supplier, label: `🏪 ${supplier}` }))}
                          sortKey="supplier_name"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="factura"
                          title="Factura"
                          filterKey="invoice"
                          currentValue={filters.invoice}
                          options={[]}
                          sortKey="invoice_number"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        {/* Columna Acciones eliminada a solicitud del usuario */}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {currentEntries.map((entry) => (
                        <motion.tr
                          key={entry.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.3 }}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleViewEntry(entry)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm font-medium text-gray-900">
                              #{entry.id}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 mb-1">
                              {entry.product_count || 0} productos
                            </div>
                            {entry.batch_id && (
                              <div className="text-xs text-gray-500 mb-1">Lote: {entry.batch_id}</div>
                            )}
                            {/* Lista completa de productos */}
                            {entry.products && entry.products.length > 0 ? (
                              <div className="text-xs text-gray-600 space-y-1 mt-2 max-h-32 overflow-y-auto">
                                {entry.products.map((product, idx) => (
                                  <div key={idx} className="flex items-center">
                                    <div className="h-2 w-2 rounded-full bg-blue-500 mr-1 flex-shrink-0"></div>
                                    <span className="truncate max-w-[200px]">{product.product_name}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 italic mt-1">Ver detalle para productos</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-900 font-medium">
                              ${entry.total_cost ? parseFloat(entry.total_cost).toFixed(2) : '0.00'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-900">
                              {entry.entry_date ? (() => {
                                const date = new Date(entry.entry_date);
                                const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                                const localDate = new Date(date.getTime() + userTimezoneOffset);
                                return localDate.toLocaleDateString();
                              })() : 'Sin fecha'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-900">
                              {entry.supplier_name || getSupplierName(entry.supplier_id) || 'Sin proveedor'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-900">
                              {entry.invoice_number || 'Sin factura'}
                            </div>
                          </td>
                          {/* Celda Acciones eliminada */}
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center space-x-2">
                    <motion.button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        page === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105'
                      }`}
                      whileHover={page !== 1 ? { scale: 1.05 } : {}}
                      whileTap={page !== 1 ? { scale: 0.95 } : {}}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </motion.button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                        <motion.button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            page === pageNum
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                          }`}
                          whileHover={page !== pageNum ? { scale: 1.05 } : {}}
                          whileTap={{ scale: 0.95 }}
                        >
                          {pageNum}
                        </motion.button>
                      ))}
                    </div>
                    
                    <motion.button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        page === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105'
                      }`}
                      whileHover={page !== totalPages ? { scale: 1.05 } : {}}
                      whileTap={page !== totalPages ? { scale: 0.95 } : {}}
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default EntriesPage;
